import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX } from "@/api/client";

/** HQ staff: Super admin / Admin / Employee and module access (backend routes/staff-hq.ts, i105). */

export type StaffTier = "super_admin" | "admin" | "employee";

export interface StaffGrant {
  module: string;
  action: string;
}

export interface StaffMe {
  tier: StaffTier | null;
  can_manage_staff: boolean;
  /** null for the Super admin, who holds everything. */
  grants: string[] | null;
  all_access: boolean;
}

export interface CatalogueModule {
  module: string;
  label: string;
  description: string;
  min_tier: StaffTier;
  sensitive: boolean;
  work_scope: boolean;
  actions: { action: string; tickable: boolean }[];
}

export interface Catalogue {
  actor_tier: StaffTier | null;
  tiers: ("admin" | "employee")[];
  modules: CatalogueModule[];
}

export interface StaffMember {
  user_id: string;
  display_name: string | null;
  email: string | null;
  public_id: string | null;
  tier: StaffTier | null;
  legacy_role: string | null;
  active: boolean;
  assignment_id: string | null;
  status: string | null;
  since: string | null;
  grants: StaffGrant[];
}

export interface StaffHistoryEntry {
  id: string;
  at: string;
  by: string | null;
  action: string;
  reason: string | null;
  prior: { tier?: string | null; grants?: string[]; status?: string } | null;
  after: { tier?: string; grants?: string[]; added?: string[]; removed?: string[]; status?: string } | null;
}

const BASE = `${API_VERSION_PREFIX}/hq/staff`;

export function createStaffApi(client: ApiClient) {
  return {
    me: () => client.get<StaffMe>(`${BASE}/me`),
    catalogue: () => client.get<Catalogue>(`${BASE}/catalogue`),
    list: () => client.get<{ items: StaffMember[]; actor_tier: StaffTier | null }>(BASE),
    get: (userId: string) => client.get<{ member: StaffMember; history: StaffHistoryEntry[] }>(`${BASE}/${userId}`),
    create: (body: { email: string; display_name: string; tier: "admin" | "employee"; grants: StaffGrant[]; reason?: string | null }) =>
      client.post<{ user_id: string; created_login: boolean; invite_email: "sent" | "failed" | null }>(BASE, body),
    setAccess: (userId: string, body: { tier: "admin" | "employee"; grants: StaffGrant[]; reason?: string | null }) =>
      client.request<{ result: unknown }>(`${BASE}/${userId}/access`, { method: "PUT", body: JSON.stringify(body) }),
    deactivate: (userId: string, reason: string) => client.post<{ result: unknown }>(`${BASE}/${userId}/deactivate`, { reason }),
    reactivate: (userId: string) => client.post<{ result: unknown }>(`${BASE}/${userId}/reactivate`, {}),
    resendInvite: (userId: string) => client.post<{ invite_email: "sent" | "failed" }>(`${BASE}/${userId}/resend-invite`, {}),
  };
}

export const TIER_LABEL: Record<StaffTier, string> = {
  super_admin: "Super admin",
  admin: "Admin",
  employee: "Employee",
};

export const ACTION_LABEL: Record<string, string> = {
  view: "View",
  manage: "Manage",
  view_all: "See everyone's work",
};
